import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Block, Check, Undo } from '@mui/icons-material';

import {
  IArnoBlockHit,
  IArnoUnresolvedReference,
  useArnoBlockSearch,
  useResetArnoReference,
  useResolveArnoReference,
} from '../../hooks/reactQuerys/ArnoAdminQueries';
import { blockLeafName, splitReferenceKey } from './arnoAdminLabels';
import { TechnicalDetails } from './TechnicalDetails';
import { useAlert } from '../../context/AlertContext';

/**
 * Einen Legacy-Verweis von Hand auflösen (#1245).
 *
 * ArnoTop verweist über `Kategorie/Bereich/Bausteinname`. Wo dieser Name im
 * Export nicht mehr existiert, bleibt der Verweis offen — geraten wird nichts.
 * Hier wird er auf einen echten Baustein gesetzt oder bewusst offen gelassen.
 * Der alte Schlüssel bleibt im Baustein stehen: er ist die Quelle und wird
 * nicht umgeschrieben.
 */

interface Props {
  reference: IArnoUnresolvedReference | null;
  onClose: () => void;
}

export const ReferenceResolveDialog: React.FC<Props> = ({
  reference,
  onClose,
}) => {
  const { showAlert } = useAlert();
  const [search, setSearch] = useState('');
  const [blockKey, setBlockKey] = useState('');
  const [note, setNote] = useState('');

  const { blockHits, isSearchingBlocks } = useArnoBlockSearch(search);
  const { resolveReference, isResolvingReference } = useResolveArnoReference();
  const { resetReference, isResettingReference } = useResetArnoReference();

  useEffect(() => {
    setBlockKey(reference?.resolvedBlockKey ?? '');
    setNote(reference?.note ?? '');
    // Der Bausteinname ist der beste erste Suchbegriff, den es hier gibt.
    setSearch(reference ? (reference.blockName ?? '') : '');
  }, [reference]);

  const busy = isResolvingReference || isResettingReference;
  const parts = reference ? splitReferenceKey(reference.referenceKey) : null;

  const save = async (status: 'CONFIRMED' | 'IGNORED') => {
    if (!reference) {
      return;
    }
    try {
      await resolveReference({
        referenceKey: reference.referenceKey,
        blockKey: status === 'IGNORED' ? null : blockKey,
        status,
        note: note.trim() || null,
      });
      showAlert({
        severity: 'success',
        text:
          status === 'IGNORED'
            ? 'Der Verweis bleibt bewusst offen.'
            : `Der Verweis zeigt jetzt auf ${blockKey}.`,
      });
      onClose();
    } catch (error) {
      showAlert({
        severity: 'error',
        text:
          error instanceof Error
            ? `Speichern fehlgeschlagen: ${error.message}`
            : 'Speichern fehlgeschlagen.',
      });
    }
  };

  const reset = async () => {
    if (!reference) {
      return;
    }
    try {
      await resetReference({ referenceKey: reference.referenceKey });
      showAlert({
        severity: 'success',
        text: 'Der Verweis gilt wieder als offen.',
      });
      onClose();
    } catch (error) {
      showAlert({
        severity: 'error',
        text:
          error instanceof Error
            ? `Zurücksetzen fehlgeschlagen: ${error.message}`
            : 'Zurücksetzen fehlgeschlagen.',
      });
    }
  };

  return (
    <Dialog
      open={Boolean(reference)}
      onClose={busy ? undefined : onClose}
      maxWidth='md'
      fullWidth
    >
      <DialogTitle>
        {parts?.blockName}
        <Typography variant='caption' display='block' color='text.secondary'>
          {parts?.category} · {parts?.area}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity='info'>
            Der alte Schlüssel bleibt in den Bausteinen stehen. Die Auflösung
            wird daneben gespeichert und überlebt jeden Import — nichts wird
            still umgeschrieben.
          </Alert>

          <Box>
            <Typography variant='subtitle2'>Wo der Verweis steht</Typography>
            <Typography variant='body2' color='text.secondary'>
              {reference?.occurrences ?? 0} Vorkommen
              {reference?.referenceKinds.length
                ? ` · ${reference.referenceKinds.join(', ')}`
                : ''}
            </Typography>
            <Stack
              direction='row'
              spacing={0.5}
              sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}
            >
              {(reference?.owners ?? []).slice(0, 12).map(owner => (
                <Chip
                  key={owner}
                  size='small'
                  variant='outlined'
                  label={owner}
                />
              ))}
            </Stack>
          </Box>

          {(reference?.candidates ?? []).length > 0 && (
            <Box>
              <Typography variant='subtitle2'>
                Namensähnliche Bausteine
              </Typography>
              <Stack
                direction='row'
                spacing={0.5}
                sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}
              >
                {(reference?.candidates ?? []).map(candidate => (
                  <Chip
                    key={candidate}
                    size='small'
                    label={blockLeafName(candidate)}
                    onClick={() => {
                      setBlockKey(candidate);
                      setSearch(candidate);
                    }}
                  />
                ))}
              </Stack>
              <Typography variant='caption' color='text.secondary'>
                Vorschläge nach Namensähnlichkeit, keine Aussage über den Inhalt
                — bitte prüfen.
              </Typography>
            </Box>
          )}

          <Autocomplete<IArnoBlockHit, false, false, true>
            freeSolo
            options={blockHits}
            loading={isSearchingBlocks}
            inputValue={search}
            value={blockKey || null}
            disabled={busy}
            getOptionLabel={option =>
              typeof option === 'string' ? option : option.blockKey
            }
            isOptionEqualToValue={(option, current) =>
              option.blockKey ===
              (typeof current === 'string' ? current : current.blockKey)
            }
            onInputChange={(_event, next) => setSearch(next)}
            onChange={(_event, next) =>
              setBlockKey(
                typeof next === 'string' ? next : (next?.blockKey ?? '')
              )
            }
            renderOption={(props, option) => (
              <Box component='li' {...props} key={option.blockKey}>
                <Box>
                  <Typography variant='body2'>{option.name}</Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {option.category} · {option.area}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={parameters => (
              <TextField
                {...parameters}
                size='small'
                label='Zielbaustein suchen'
                helperText='Mindestens zwei Zeichen; gesucht wird im aktiven Modell.'
              />
            )}
          />

          <TextField
            size='small'
            label='Notiz (warum dieser Baustein?)'
            value={note}
            onChange={event => setNote(event.target.value)}
            disabled={busy}
            multiline
            minRows={2}
          />

          <TechnicalDetails
            entries={[
              { label: 'referenceKey', value: reference?.referenceKey },
              { label: 'category', value: parts?.category },
              { label: 'area', value: parts?.area },
              { label: 'blockName', value: parts?.blockName },
              {
                label: 'referenceKinds',
                value: reference?.referenceKinds.join(', '),
              },
              {
                label: 'owners',
                value: reference?.owners.join(', '),
              },
              { label: 'state', value: reference?.state },
              { label: 'mappingStatus', value: reference?.mappingStatus },
              { label: 'resolvedBlockKey', value: reference?.resolvedBlockKey },
            ]}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Tooltip
          title={
            reference?.state === 'open'
              ? 'Es gibt keine Entscheidung, die zurückgenommen werden könnte.'
              : 'Entscheidung zurücknehmen — der Verweis gilt wieder als offen.'
          }
        >
          <span>
            <Button
              startIcon={<Undo />}
              onClick={reset}
              disabled={busy || reference?.state === 'open'}
            >
              Zurücksetzen
            </Button>
          </span>
        </Tooltip>

        <Stack direction='row' spacing={1}>
          <Button onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button
            startIcon={<Block />}
            onClick={() => save('IGNORED')}
            disabled={busy}
          >
            Bewusst offen lassen
          </Button>
          <Tooltip
            title={blockKey ? '' : 'Bitte zuerst einen Baustein wählen.'}
          >
            <span>
              <Button
                variant='contained'
                startIcon={busy ? <CircularProgress size={16} /> : <Check />}
                onClick={() => save('CONFIRMED')}
                disabled={busy || !blockKey}
              >
                Verweis auflösen
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
